import { useEffect, useState } from "react";
import BasicCheckbox from "../../../components/basicCheckbox/basicCheckbox";
import BasicInput from "../../../components/inputs/basicInput/basicInput";
import BasicPanel from "../../../components/panels/basicPanel/basicPanel";
import { useInvoiceContext } from "../createInvoiceContext";

import "./calculationInvoice.css";
import { Panel } from "rsuite";
import { getCurrencySymbol } from "../../../utils/currencyHelper";
import { getCurrencyRate } from "../../../api/currency";
import { useTranslation } from "react-i18next";

const CARET_SVG =
    <svg width="14" height="9" viewBox="0 0 14 9" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M0.298162 8.13654C-0.0659236 7.78507 -0.0659236 7.21522 0.298163 6.86375L6.51351 0.863751C6.8776 0.512278 7.4679 0.512278 7.83198 0.863751L13.702 6.53042C14.0661 6.88189 14.0661 7.45174 13.702 7.80321C13.3379 8.15468 12.7476 8.15468 12.3836 7.80321L7.17275 2.77294L1.61664 8.13654C1.25255 8.48801 0.66225 8.48801 0.298162 8.13654Z" fill="#17C653"/>
    </svg>

const LabelWithSubLabel = ({ label, subLabel }) => {
    return (
        <div className={`labelWithSubLabel`} style={{width: "max-content"}}>
            <div className="paragraph-14R" style={{color: "var(--Gray-800)"}}>{label}</div>
            <div className="paragraph-10R">{subLabel}</div>
        </div>
    )
}

const LabelWithValueAndKey = ({ label, subLabel, value }) => {
    return (
        <div className={`labelWithValueAndKey`}>
            <LabelWithSubLabel label={label} subLabel={subLabel} />
            <div className="paragraph-14R" style={{
                textWrap: "nowrap",
            }}>{value}</div>
        </div>
    )
}

const getPercent = (userInfo) => {
    if (userInfo.contract_type == 2) {
        return 94
    }

    return 96
}

const roundToTwo = (num) => Math.round(num * 100) / 100

function sanitizeInput(value) {
    return value.replace(/[^0-9.,]/g, '');
  }

  function fixDecimal(value) {
    let result = '';
    let decimalUsed = false;
  
    for (let i = 0; i < value.length; i++) {
      const ch = value[i];
      if (ch === '.' || ch === ',') {
        if (!decimalUsed) {
          // First decimal separator becomes '.' 
          decimalUsed = true;
          result += '.';
        }
        // If we already used a decimal separator, skip additional ones
      } else {
        result += ch;
      }
    }
  
    return result;
  }
  
  /**
   * A "fully typed" float must:
   *   - Not be empty
   *   - Contain digits
   *   - Optionally have one decimal point
   *   - Not end in decimal point
   * Examples of "fully typed": "5", "5.0", "123.456"
   * Examples of partial: "5.", "", ".5"
   */
  function isFullyTypedFloat(value) {
    if (!value) return false;
    if (value.endsWith('.')) return false; // trailing decimal -> partial
    // Must match digits, optional decimal, digits
    return /^\d+(\.\d+)?$/.test(value);
  }
  
  /**
   * Main function:
   *   - Remove invalid chars
   *   - Normalize decimal
   *   - If fully typed float, parse & return as number
   *   - Otherwise keep partial string
   */
  function getFloatValue(value) {
    // 1. Clean any invalid chars
    let cleaned = sanitizeInput(value);
  
    // 2. Normalize decimal separators to a single '.'
    cleaned = fixDecimal(cleaned);
  
    // 3. If it's a fully typed float, parse it
    if (isFullyTypedFloat(cleaned)) {
      const num = parseFloat(cleaned);
      // If you want to disallow 0 or negative, clamp to 0 or do something else:
      return num > 0 ? num : 0;
    }
  
    // 4. Partial input => return the (cleaned) string so user can keep typing
    return cleaned;
  }

const GoodPanel = ({ good, currencySymbol }) => {
    const { t } = useTranslation('basic');
    
    return (
        <Panel bodyFill collapsible
        caretAs={() => <span className="panelCaret">{CARET_SVG}</span>}
        header={
            <LabelWithSubLabel label={<span className="paragraph-14M">{good.name}</span>} subLabel={good.count + " " + t("pcs")} />
        }
        className="goodPanel">
            <div className="goodPanelRows">
                    <div className="goodPanelRowItem">
                        <span className="paragraph-14R" style={{color: "var(--Gray-600)"}}>
                            {t("insurance_per_unit")}
                        </span>
                        <span className="paragraph-14R">
                            {good.insurance + " " + currencySymbol}
                        </span>
                    </div>

                    <div className="goodPanelRowItem">
                        <span className="paragraph-14R" style={{color: "var(--Gray-600)"}}>
                            {t("additional_charge_cod")}
                        </span>
                        <span className="paragraph-14R">
                            {good.price ? (good.price + " " + currencySymbol) : t("no")}
                        </span>
                    </div>
            </div>
        </Panel>
    )
}

const PlacePanel = ({ place, currencySymbol, t }) => {
    return (
        <BasicPanel className="placePanel" bodyFill header={<div>{t("place_word")} {place.number}</div>}>
            {place.goods.map((good, index) => <GoodPanel key={index} good={good} currencySymbol={currencySymbol} />)}
        </BasicPanel>
    )
}

export default function InvoiceCalculation() {
    const { t } = useTranslation('basic');
    const { invoiceData, setInvoiceData } = useInvoiceContext();
    const [refresh, setRefresh] = useState(0);
    const currencySymbol = getCurrencySymbol(invoiceData?.user_info);
    const [currencyRate, setCurrencyRate] = useState(null); // KZT rate relative to RUB
    
    // Load currency rate for conversion (always load for proper conversions)
    useEffect(() => {
        const loadCurrencyRate = async () => {
            try {
                const rate = await getCurrencyRate('KZT');
                const rateValue = rate.value; // e.g., 6.0 (1 RUB = 6 KZT)
                setCurrencyRate(rateValue);
                // Store rate in invoiceData for use in arrangeInvoiceParams
                setInvoiceData(oldData => ({...oldData, currencyRate: rateValue}));
            } catch (error) {
                console.error('Failed to load currency rate:', error);
                const fallbackRate = 6.0;
                setCurrencyRate(fallbackRate);
                setInvoiceData(oldData => ({...oldData, currencyRate: fallbackRate}));
            }
        };
        
        loadCurrencyRate();
    }, []);

    useEffect(() => {
        console.log("invoiceData", invoiceData)
    }, [refresh]);

    // calculations
    useEffect(() => {
        let all_goods_prices = 0;
        let all_goods_price_alter = 0;

        let service_price = invoiceData.additions_sum || 0;
        let delivery_price = invoiceData.delivery_sum || 0;

        const insurance_value = invoiceData?.user_info?.insurance_procent / 10000 * (invoiceData?.user_info?.contract_vat || 1);

        invoiceData.places.forEach(place => {
            place.goods.forEach(good => {
                all_goods_prices += good.insurance * good.count;
                all_goods_price_alter += good.price * good.count;
            })
        });

        console.log("Сумма цены товаров: ", all_goods_prices)
        console.log("Сумма цены товаров с НДС: ", all_goods_price_alter)
        console.log("Сумма доставки: ", delivery_price)
        console.log("Сумма услуг: ", service_price)
        console.log("Сумма страхования: ", insurance_value * all_goods_prices)
        console.log("Значение страхования: ", insurance_value)

        let XAG = 0
        let ADOPSBOR = 0
        let XAG1;

        if (invoiceData.user_info.contract_type == 2) {
            XAG1 = (all_goods_price_alter / 100) * 5;
        } else {
            XAG1 = (all_goods_price_alter / 100) * 3;
        }

        let delivery_pay = 0;
        let contract_pay = 0;

        // Оплата при получении, комиссия, доставка
        if (invoiceData.payment_on_receipt && invoiceData.commission && invoiceData.delivery_pay) {
            XAG = (
                    ( (delivery_price + service_price) + ( insurance_value * all_goods_prices + all_goods_price_alter) )
                    / getPercent(invoiceData.user_info) * 100
                )
                - ( ( delivery_price + service_price) + ( insurance_value * all_goods_prices + all_goods_price_alter) );
      
            if (invoiceData.hand_sum) {
                ADOPSBOR = parseFloat(invoiceData.hand_sum);
            } else {
                ADOPSBOR = Math.ceil(XAG + delivery_price + service_price + insurance_value * all_goods_prices);
            }

            delivery_pay = ADOPSBOR + all_goods_price_alter;
            contract_pay = 0;

            console.log(delivery_pay)
        }

        // Оплата при получении, нет комиссии, нет доставки. НЕ ПОН
        if (invoiceData.payment_on_receipt && !invoiceData.commission && !invoiceData.delivery_pay) {
            if (invoiceData.hand_sum) {
                ADOPSBOR = parseFloat(invoiceData.hand_sum);
            } else {
                ADOPSBOR = 0;
            }
        
            delivery_pay = ADOPSBOR + all_goods_price_alter;
            contract_pay = insurance_value * all_goods_prices + XAG1 + delivery_price + service_price;
        }

        // Оплата при получении, комиссия, нет доставки
        if (invoiceData.payment_on_receipt && invoiceData.commission && !invoiceData.delivery_pay) {
            XAG = all_goods_price_alter / getPercent(invoiceData.user_info) * 100 - all_goods_price_alter;

            if (invoiceData.hand_sum) {
                ADOPSBOR = parseFloat(invoiceData.hand_sum);
            } else {
                ADOPSBOR = Math.ceil(XAG);
            }

            delivery_pay = all_goods_price_alter + ADOPSBOR;
            contract_pay = delivery_price + service_price + insurance_value * all_goods_prices;
        }

        // Оплата при получении, нет комиссии, доставка
        if (invoiceData.payment_on_receipt && !invoiceData.commission && invoiceData.delivery_pay) {
            XAG = (
                    ( (delivery_price + service_price ) + ( insurance_value * all_goods_prices + all_goods_price_alter) )
                    / getPercent(invoiceData.user_info) * 100
                )
                - ( ( delivery_price + service_price ) + ( insurance_value * all_goods_prices + all_goods_price_alter ) );
      
            if (invoiceData.hand_sum) {
                ADOPSBOR = parseFloat(invoiceData.hand_sum);
            } else {
                ADOPSBOR = Math.ceil(delivery_price + service_price + ( insurance_value * all_goods_prices ));
            }

            delivery_pay = all_goods_price_alter + ADOPSBOR;
            contract_pay = XAG;
        }

        // Нет оплаты при получении, комиссия, доставка
        if (!invoiceData.payment_on_receipt && invoiceData.commission && invoiceData.delivery_pay) {
            XAG = (
                    ( ( delivery_price + service_price ) + ( insurance_value * all_goods_prices ) )
                    / getPercent(invoiceData.user_info) * 100
                ) - ( ( delivery_price + service_price ) + ( insurance_value * all_goods_prices ) ); 
      
            if (invoiceData.hand_sum) {
                ADOPSBOR = parseFloat(invoiceData.hand_sum);
            } else {
                ADOPSBOR = XAG + delivery_price + service_price + ( insurance_value * all_goods_prices );
            }

            delivery_pay = ADOPSBOR;
            contract_pay = 0;
        }

        // Нет оплаты при получении, комиссия, нет доставки
        if (!invoiceData.payment_on_receipt && invoiceData.commission && !invoiceData.delivery_pay) {
            XAG = (
                    (( delivery_price + service_price) +( insurance_value * all_goods_prices))
                    / getPercent(invoiceData.user_info) * 100
                ) - ( ( delivery_price + service_price ) + ( insurance_value * all_goods_prices ) ); 
      
            if (invoiceData.hand_sum) {
                ADOPSBOR = parseFloat(invoiceData.hand_sum);
            } else {
                ADOPSBOR = 0;
            }

            delivery_pay = ADOPSBOR;
            contract_pay = delivery_price + service_price + ( insurance_value * all_goods_prices);
        }

        // Нет оплаты при получении, нет комиссии, доставка
        if (!invoiceData.payment_on_receipt && !invoiceData.commission && invoiceData.delivery_pay) {
            XAG = (
                    ( ( delivery_price + service_price ) + ( insurance_value * all_goods_prices ) )
                    / getPercent(invoiceData.user_info) * 100
                ) - ( ( delivery_price + service_price ) +( insurance_value * all_goods_prices ) ); 
      
            if (invoiceData.hand_sum) {
                ADOPSBOR = parseFloat(invoiceData.hand_sum);
            } else {
                ADOPSBOR = Math.ceil(delivery_price + service_price + ( insurance_value * all_goods_prices));
            }

            delivery_pay = ADOPSBOR;
            contract_pay = XAG;
        }

        // Нет оплаты при получении, нет комиссии, нет доставки
        if (!invoiceData.payment_on_receipt && !invoiceData.commission && !invoiceData.delivery_pay) {
            XAG = (
                ( ( delivery_price + service_price ) + ( insurance_value * all_goods_prices ) )
                / getPercent(invoiceData.user_info) * 100
            ) - ( ( delivery_price + service_price ) + ( insurance_value * all_goods_prices ) ); 
      
            if (invoiceData.hand_sum) {
                ADOPSBOR = parseFloat(invoiceData.hand_sum);
            } else {
                ADOPSBOR = 0;
            }

            delivery_pay = ADOPSBOR;
            contract_pay = delivery_price + service_price + ( insurance_value * all_goods_prices);
        }

        // Additional fee for payment on receipt based on route and contract type.
        // Rules per business diagram (sender can be any EAEU country):
        //   RF → KZ, KZ contract only: +250 KZT
        //   KZ → RF, KZ contract only: +50 RUB (sent as KZT so backend KZT→RUB gives 50 RUB)
        //   All other EAEU routes (EAEU→KZ, EAEU→RF, KZ→KZ, etc.): no extra fee
        let additional_fee = 0; // In contract currency (for API)
        let additional_fee_display = 0; // For display in contract currency

        if (invoiceData.payment_on_receipt && currencyRate) {
            const cityFrom = invoiceData.cityFromCountryCode;
            const cityTo = invoiceData.cityToCountryCode;
            const isKZContract = invoiceData?.user_info?.is_kz;

            if (cityFrom === 1 && cityTo === 48 && isKZContract) {
                // RF → KZ, KZ contract: +250 KZT
                additional_fee = 250;
                additional_fee_display = 250;
            } else if (cityFrom === 48 && cityTo === 1 && isKZContract) {
                // KZ → RF, KZ contract: +50 RUB expressed in KZT
                additional_fee = Math.ceil(50 * currencyRate);
                additional_fee_display = Math.ceil(50 * currencyRate);
            }
            // All other routes: no additional fee (additional_fee stays 0)
        }

        // Add additional fee to delivery_pay
        delivery_pay = roundToTwo(delivery_pay + additional_fee);

        // Currency conversion for delivery_pay (к оплате с получателя) based on contract and destination
        // User always sees values in contract currency (KZ=tenge, non-KZ=rubles)
        // But API needs the value in the destination country's currency when payment_on_receipt is true
        // 
        // Conversion rules:
        // 1. KZ contract + cityTo NOT in KZ: Display tenge → API gets rubles
        // 2. KZ contract + cityTo in KZ: Display tenge → API gets tenge (no conversion)
        // 3. NOT KZ contract + cityTo in KZ: Display rubles → API gets tenge
        // 4. NOT KZ contract + cityTo NOT in KZ: Display rubles → API gets rubles (no conversion)
        let delivery_pay_for_api = delivery_pay;

        // set pays
        setInvoiceData(oldData => {
            const newData = { ...oldData };
            newData.delivery_pay_amount = delivery_pay; // For display in contract currency
            newData.delivery_pay_amount_for_api = roundToTwo(delivery_pay_for_api); // For API in destination currency
            newData.contract_pay = roundToTwo(contract_pay);
            newData.additional_sum = invoiceData.payment_on_receipt ? roundToTwo(ADOPSBOR + additional_fee) : 0;
            newData.additional_fee = additional_fee; // Store for API
            newData.additional_fee_display = additional_fee_display; // Store for display
            return newData;
        })
        setRefresh(refresh => refresh + 1);
    }, [invoiceData.commission, invoiceData.delivery_pay, invoiceData.hand_sum, invoiceData.payment_on_receipt, invoiceData.cityFromCountryCode, invoiceData.cityToCountryCode, invoiceData?.user_info?.is_kz, currencyRate]);

    return (
        <div className="invoiceCalculationWrapper">
            <BasicPanel header={t("calculation")} className="invoiceCalculation">
                <BasicCheckbox
                    disabled={!invoiceData.payment_on_receipt}
                    label={t("commission_from_receiver")}
                    checked={invoiceData.commission}
                    onChange={(value) => {
                        setInvoiceData(oldData => {
                            const newData = { ...oldData };
                            newData.commission = value;
                            return newData;
                        });

                        setRefresh(refresh => refresh + 1);
                    }}
                />
                
                <BasicCheckbox
                    disabled={!invoiceData.payment_on_receipt}
                    label={t("delivery_from_receiver")}
                    size="lg"
                    checked={invoiceData.delivery_pay}
                    onChange={(value) => {
                        setInvoiceData(oldData => {
                            const newData = { ...oldData };
                            newData.delivery_pay = value;
                            return newData;
                        });

                        setRefresh(refresh => refresh + 1);
                    }}
                />
            </BasicPanel>

            <BasicPanel>
                    <div className="calculationInfo">
                        <LabelWithValueAndKey
                            label={t("payment_from_receiver")}
                            subLabel={t("payment_from_receiver_hint")} 
                            value={Math.ceil(invoiceData.delivery_pay_amount || 0) + " " + currencySymbol} />

                        <LabelWithValueAndKey
                            label={t("payment_by_contract")}
                            subLabel={t("payment_by_contract_hint")} 
                            value={Math.ceil(invoiceData.contract_pay || 0) + " " + currencySymbol} />
                        
                        <BasicInput
                            disabled={!invoiceData.payment_on_receipt}
                            label={t("additional_collection_from_receiver")}
                            placeholder={invoiceData.additional_sum}
                            helpTextHighlight={!invoiceData.payment_on_receipt}
                            helpText={t("additional_collection_hint")}
                            addonAfter={currencySymbol}
                            value={invoiceData.hand_sum || ""}
                            onChange={(value) => {
                                if (!invoiceData.payment_on_receipt) return;
                                
                                setInvoiceData(oldData => {
                                    const newData = { ...oldData };
                                    newData.hand_sum = getFloatValue(value);
                                    return newData;
                                })
                            }} />
                    </div>
            </BasicPanel>

            {
                invoiceData.places.map((place, index) => <PlacePanel key={index} place={{...place, number: index + 1}} currencySymbol={currencySymbol} t={t} />)
            }
        </div>
    )
}